import React, { useEffect, useState } from 'react';
import { Download, X, Smartphone, Share2 } from 'lucide-react';

/*
 * Cross-platform "Install NutriFitness" prompt.
 *
 * Behaviour:
 *   - Android/Chrome: uses beforeinstallprompt to trigger the native install dialog.
 *   - iOS Safari: shows a banner with "Share → Add to Home Screen" instructions
 *     (iOS does not expose a programmatic install API).
 *
 * Re-prompt policy:
 *   - If the user installs, we never show it again (detected via `appinstalled` event
 *     and `display-mode: standalone`).
 *   - If the user taps "X" (dismiss), we hide it for 24 h only, so they get another
 *     chance the next day (previously 7 days — too long, users forgot).
 *   - If the user accepts the native dialog then cancels at the OS level, we wait 24 h.
 *
 * Tracking (fire-and-forget): prompt_shown, install_clicked, install_accepted,
 *   install_dismissed, install_completed, ios_hint_shown — feeds the Admin Funnel.
 */
const DISMISS_KEY = 'pwa_install_dismissed_until';
const INSTALLED_KEY = 'pwa_installed';
const SESSION_KEY = 'pwa_session_id';
const DISMISS_MS = 3 * 60 * 1000; // 3 minutes — re-prompt aggressively until installed
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true);

const isCapacitorNative = () =>
  typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.() === true;

const isIOS = () => {
  const ua = navigator.userAgent || '';
  return /iPhone|iPad|iPod/.test(ua) && !window.MSStream;
};

const detectPlatform = () => {
  const ua = (navigator.userAgent || '').toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  if (/android/.test(ua)) return 'android';
  return 'desktop';
};

const getSessionId = () => {
  let sid = sessionStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `pwa_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
    sessionStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const trackEvent = (event) => {
  if (!BACKEND_URL) return;
  try {
    const payload = {
      event,
      platform: detectPlatform(),
      user_agent: navigator.userAgent || '',
      session_id: getSessionId(),
    };
    fetch(`${BACKEND_URL}/api/analytics/pwa-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {}
};

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHint, setShowIOSHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || isCapacitorNative()) {
      if (localStorage.getItem(INSTALLED_KEY) !== 'true') {
        localStorage.setItem(INSTALLED_KEY, 'true');
        trackEvent('install_completed');
      }
      return;
    }
    if (localStorage.getItem(INSTALLED_KEY) === 'true') return;

    const dismissedUntil = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (Date.now() < dismissedUntil) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setTimeout(() => {
        setVisible(true);
        trackEvent('prompt_shown');
      }, 3000);
    };

    const onInstalled = () => {
      localStorage.setItem(INSTALLED_KEY, 'true');
      trackEvent('install_completed');
      setVisible(false);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    if (isIOS()) {
      setTimeout(() => {
        setShowIOSHint(true);
        setVisible(true);
        trackEvent('ios_hint_shown');
      }, 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    trackEvent('install_clicked');
    try {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      setDeferredPrompt(null);
      if (outcome === 'accepted') {
        trackEvent('install_accepted');
        localStorage.setItem(INSTALLED_KEY, 'true');
        setVisible(false);
      } else {
        trackEvent('install_dismissed');
        localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
        setVisible(false);
      }
    } catch (err) {
      console.error('PWA install failed:', err);
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    trackEvent('install_dismissed');
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 pointer-events-none"
    >
      <div className="bg-gradient-to-br from-[#FF6600] to-[#FF8533] rounded-2xl shadow-2xl p-6 text-white w-full max-w-xs pointer-events-auto animate-slide-up relative">
        <button
          data-testid="pwa-install-dismiss"
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-white/80 hover:text-white p-1"
          aria-label="Dismiss"
        >
          <X size={20} />
        </button>

        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl bg-white/20 flex items-center justify-center mx-auto mb-3">
            <Smartphone size={28} />
          </div>
          <h3 className="font-bold text-lg mb-4">Install our app</h3>

          {showIOSHint ? (
            <div data-testid="pwa-install-ios-instructions" className="text-left text-sm text-white/95 space-y-3">
              <p className="text-center text-xs text-white/80 mb-3">
                To install on iPhone, follow these 2 steps in Safari:
              </p>
              <div className="flex items-start gap-3 bg-white/10 rounded-xl p-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white text-[#FF6600] font-bold flex items-center justify-center text-sm">1</div>
                <div className="flex-1">
                  <div className="flex items-center gap-1.5 font-semibold">
                    Tap the <Share2 size={14} className="inline-block" /> Share button
                  </div>
                  <p className="text-xs text-white/80 mt-0.5">at the bottom of Safari (square with arrow up)</p>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-white/10 rounded-xl p-3">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-white text-[#FF6600] font-bold flex items-center justify-center text-sm">2</div>
                <div className="flex-1">
                  <div className="font-semibold">Tap "Add to Home Screen"</div>
                  <p className="text-xs text-white/80 mt-0.5">scroll down in the Share menu if needed</p>
                </div>
              </div>
              <button
                data-testid="pwa-install-ios-got-it"
                onClick={handleDismiss}
                className="w-full bg-white text-[#FF6600] font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-gray-100 transition-colors mt-2"
              >
                Got it
              </button>
            </div>
          ) : (
            <button
              data-testid="pwa-install-btn"
              onClick={handleInstall}
              className="w-full bg-white text-[#FF6600] font-bold text-sm px-4 py-3 rounded-xl hover:bg-gray-100 transition-colors flex items-center justify-center gap-2"
            >
              <Download size={18} />
              Install Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
